import React, { useState, useEffect } from 'react';
import { MapPin, Tag, FileText, Info } from 'lucide-react';
import { Tenant } from '../../../core/types';
import { TenantType, CategoryTag } from '../../../core/types/portal';
import { BRAZILIAN_STATES } from '../../portal/data/brazilianStates';

// Mock list of cities for now (in production this would come from an API or a large JSON)
// Integrating with the existing BrazilianStates data
const MOCK_CITIES_BY_STATE: Record<string, string[]> = {
  'GO': ['Aruanã', 'São Miguel do Araguaia', 'Luís Alves', 'Goiânia', 'Caldas Novas'],
  'MT': ['Cuiabá', 'Cáceres', 'Poconé', 'Barão de Melgaço'],
  'AM': ['Manaus', 'Barcelos', 'Santa Isabel do Rio Negro'],
  'MS': ['Corumbá', 'Bonito', 'Miranda', 'Aquidauana'],
  'TO': ['Palmas', 'Lagoa da Confusão', 'Caseara']
};

interface PortalDataStepProps {
  data: Partial<Tenant>;
  onChange: (updates: Partial<Tenant>) => void;
}

const CATEGORY_TAGS: { value: CategoryTag; label: string }[] = [
  { value: 'pesca-embarcada', label: 'Pesca Embarcada' },
  { value: 'pesca-de-barranco', label: 'Pesca de Barranco' },
  { value: 'fly-fishing', label: 'Fly Fishing (Mosca)' },
  { value: 'pesca-submersa', label: 'Pesca Submersa' },
  { value: 'pesca-esportiva', label: 'Pesca Esportiva' },
  { value: 'catch-and-release', label: 'Pesque e Solte' },
  { value: 'all-inclusive', label: 'All Inclusive' },
  { value: 'guiada', label: 'Pesca Guiada' },
  { value: 'equipamentos-inclusos', label: 'Equipamentos Inclusos' },
  { value: 'pet-friendly', label: 'Pet Friendly' },
  { value: 'acessivel', label: 'Acessibilidade' }
];

const TENANT_TYPES: { value: TenantType; label: string }[] = [
  { value: 'pousada', label: 'Pousada / Lodge' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'barco-hotel', label: 'Barco Hotel' },
  { value: 'pesqueiro', label: 'Pesqueiro' },
  { value: 'guia', label: 'Guia de Pesca' },
  { value: 'agencia', label: 'Agência de Turismo' },
  { value: 'loja', label: 'Loja de Pesca' }
];

export const PortalDataStep: React.FC<PortalDataStepProps> = ({ data, onChange }) => {
  const [cities, setCities] = useState<string[]>([]);

  useEffect(() => {
    if (data.location?.state) {
      setCities(MOCK_CITIES_BY_STATE[data.location.state] || []);
    }
  }, [data.location?.state]);

  const handleLocationChange = (field: 'state' | 'city', value: string) => {
    onChange({
      location: {
        ...data.location!,
        [field]: value,
        // Reset city if state changes
        ...(field === 'state' ? { city: '' } : {})
      }
    });
  };

  const toggleTag = (tag: CategoryTag) => {
    const currentTags = data.categoryTags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    onChange({ categoryTags: newTags });
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-blue-800">
        <Info className="flex-shrink-0 mt-1" size={20} />
        <div className="text-sm">
          <p className="font-semibold mb-1">Dados para o Portal Nossa Pesca</p>
          <p>Estas informações serão exibidas publicamente para atrair pescadores no diretório <strong>pescar.app</strong>.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Coluna Esquerda: Tipo e Localização */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Estabelecimento
            </label>
            <div className="relative">
              <select
                value={data.tenantType}
                onChange={(e) => onChange({ tenantType: e.target.value as TenantType })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900"
              >
                <option value="">Selecione o tipo...</option>
                {TENANT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado (UF)</label>
              <select
                value={data.location?.state || ''}
                onChange={(e) => handleLocationChange('state', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="">UF</option>
                {BRAZILIAN_STATES.map(state => (
                  <option key={state.code} value={state.code}>{state.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
              <select
                value={data.location?.city || ''}
                onChange={(e) => handleLocationChange('city', e.target.value)}
                disabled={!data.location?.state}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 text-gray-900"
              >
                <option value="">Selecione...</option>
                {cities.length > 0 ? (
                  cities.map(city => <option key={city} value={city}>{city}</option>)
                ) : (
                  <option value={data.location?.city}>{data.location?.city || 'Outra'}</option>
                )}
              </select>
            </div>
          </div>

          <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Descrição Curta (Resumo)
             </label>
             <textarea
               value={data.portalProfile?.shortDescription || ''}
               onChange={(e) => onChange({ 
                 portalProfile: { ...data.portalProfile!, shortDescription: e.target.value } 
               })}
               maxLength={200}
               placeholder="Ex: Pousada especializada em tucunarés gigantes no coração do Araguaia..."
               className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 h-32 resize-none text-gray-900"
             />
             <div className="flex justify-between text-xs text-gray-500 mt-1">
               <span>Dica: Seja direto e atrativo.</span>
               <span>{(data.portalProfile?.shortDescription?.length || 0)}/200</span>
             </div>
          </div>
        </div>

        {/* Coluna Direita: Categorias */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Tag size={16} />
            Categorias e Diferenciais
          </label>
          <div className="grid grid-cols-2 gap-3">
            {CATEGORY_TAGS.map(tag => (
              <label 
                key={tag.value}
                className={`
                  flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                  ${(data.categoryTags || []).includes(tag.value) 
                    ? 'bg-blue-50 border-blue-500 text-blue-700' 
                    : 'hover:bg-gray-50 border-gray-200'}
                `}
              >
                <input
                  type="checkbox"
                  checked={(data.categoryTags || []).includes(tag.value)}
                  onChange={() => toggleTag(tag.value)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">{tag.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
